// Active Sales Page - Manage sales the tenant is participating in
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { fetchSaleSessions, fetchSaleEnrichment } from '../../lib/queryFunctions';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Loader2, Plus, Search, Archive } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { TableToolbar } from '../../components/common/TableToolbar';
import SaleCard from '../../components/sales/SaleCard';
import { AddSaleDialog, SessionDialog, ArchiveSaleDialog } from '../../components/sales/ActiveSaleDialogs';
import { useConfirm } from '../../components/common/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';

const ActiveSalesPage = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabParam || null);
  const [addSaleDialogOpen, setAddSaleDialogOpen] = useState(false);
  const [sessionDialogOpen, setSessionDialogOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [editingSession, setEditingSession] = useState(null);
  const [expandedSales, setExpandedSales] = useState({});
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [archiveSaleDialogOpen, setArchiveSaleDialogOpen] = useState(false);
  const [saleToArchive, setSaleToArchive] = useState(null);
  const [viewHistoric, setViewHistoric] = useState(false);
  const [deadlinesVisible, setDeadlinesVisible] = useState({});

  const { user: currentUser } = useAuth();
  const isSystemAdmin = currentUser?.roles?.includes('system_owner');
  const isTenantAdmin = currentUser?.roles?.includes('tenant_system_admin') || isSystemAdmin;
  const isCardRunner = currentUser?.roles?.includes('tenant_card_runner');
  const isSaleClerk = currentUser?.roles?.includes('tenant_sale_clerk');
  const isViewOnlyUser = currentUser?.roles?.includes('tenant_other');
  const isRestrictedRole = isCardRunner || isSaleClerk;

  const [sessionForm, setSessionForm] = useState({
    sale_id: '', name: '', date: '', ship_in_date: '', barn: '', time: ''
  });

  const qc = useQueryClient();

  // ── Tier 1: tenant-sales list (cached) ────────────────────────────────────
  const { data: rawTenantSales = [], isLoading: loading } = useQuery({
    queryKey: QK.tenantSales(),
    queryFn: () => api.get('/tenant-sales').then(r => r.data),
  });
  const activeSales = rawTenantSales.filter(s => viewHistoric ? s.archived : !s.archived);

  // Keep a ref that always holds the latest activeSales so the bfcache event
  // handler (registered once) never closes over a stale snapshot of the list.
  const activeSalesRef = useRef(activeSales);
  activeSalesRef.current = activeSales;

  // ── System-wide sales (for AddSaleDialog, already fixed to filter closed) ─
  const { data: systemSales = [] } = useQuery({
    queryKey: QK.systemWideSales(),
    queryFn: () => api.get('/sales-management/system-wide').then(r => r.data),
  });

  const invalidateActiveSales = () => {
    qc.invalidateQueries({ queryKey: QK.tenantSales() });
    qc.invalidateQueries({ queryKey: QK.systemWideSales() });
  };

  // ── Tier 2: per-sale enrichment via useQueries ────────────────────────────
  // Each active sale gets its own independent cached query for sessions and
  // enrichment (opps + horses + owners + deliverables).
  const sessionQueries = useQueries({
    queries: activeSales.map(sale => ({
      queryKey: QK.saleSessions(sale.id),
      queryFn: fetchSaleSessions(sale.id),
      enabled: !!sale.id,
      staleTime: 30_000,
    })),
  });

  const enrichmentQueries = useQueries({
    queries: activeSales.map(sale => ({
      queryKey: QK.activeSaleEnrichment(sale.id),
      queryFn: fetchSaleEnrichment(sale.id),
      enabled: !!sale.id,
      staleTime: 30_000,
    })),
  });

  // Build lookup maps (index-aligned with activeSales)
  const sessionsBySaleId = useMemo(() =>
    Object.fromEntries(activeSales.map((s, i) => [s.id, sessionQueries[i]?.data || []])),
  [sessionQueries, activeSales]); // eslint-disable-line react-hooks/exhaustive-deps

  const enrichmentBySaleId = useMemo(() =>
    Object.fromEntries(activeSales.map((s, i) => [s.id, enrichmentQueries[i]?.data])),
  [enrichmentQueries, activeSales]); // eslint-disable-line react-hooks/exhaustive-deps

  // Convenience accessors matching the old state-map API (no consumer changes needed)
  const sessions              = sessionsBySaleId;
  const opportunities         = useMemo(() => Object.fromEntries(
    Object.entries(enrichmentBySaleId).map(([id, d]) => [id, d?.opportunities || []])
  ), [enrichmentBySaleId]);
  const saleOwnerDeliverables = useMemo(() => Object.fromEntries(
    Object.entries(enrichmentBySaleId).map(([id, d]) => [id, d?.saleOwnerDeliverables || []])
  ), [enrichmentBySaleId]);
  const opportunityOwners     = useMemo(() => Object.assign({},
    ...Object.values(enrichmentBySaleId).map(d => d?.opportunityOwners || {})
  ), [enrichmentBySaleId]);
  const ownerEntities         = useMemo(() => Object.assign({},
    ...Object.values(enrichmentBySaleId).map(d => d?.ownerEntities || {})
  ), [enrichmentBySaleId]);

  // Invalidation helpers for tier-2 mutations
  const invalidateSaleSessions = (saleId) =>
    qc.invalidateQueries({ queryKey: QK.saleSessions(saleId) });
  const invalidateSaleEnrichment = (saleId) =>
    qc.invalidateQueries({ queryKey: QK.activeSaleEnrichment(saleId) });

  // Handle save events from SaleOpportunityDetailPage navigated-away sessions
  useEffect(() => {
    const handler = (e) => {
      const saleId = e.detail?.sale_id;
      if (saleId) invalidateSaleEnrichment(saleId);
    };
    window.addEventListener('sale-opportunity-saved', handler);
    return () => window.removeEventListener('sale-opportunity-saved', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch when page is restored from bfcache (browser back button)
  useEffect(() => {
    const handlePageShow = (e) => {
      if (e.persisted) {
        invalidateActiveSales();
        activeSalesRef.current.forEach(s => {
          qc.invalidateQueries({ queryKey: QK.saleSessions(s.id) });
          qc.invalidateQueries({ queryKey: QK.activeSaleEnrichment(s.id) });
        });
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [viewHistoric]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleJoinSale = async (systemSale) => {
    setSaving(true);
    try {
      await api.post('/tenant-sales', { sale_id: systemSale.id });
      toast.success('Sale added to your Active Sales');
      setAddSaleDialogOpen(false);
      invalidateActiveSales();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add sale');
    } finally { setSaving(false); }
  };

  const handleDeleteSale = async (saleId) => {
    if (!await confirm({ title: 'Remove this sale from your Active Sales? Sessions will also be deleted.', destructive: true })) return;
    try {
      await api.delete(`/tenant-sales/${saleId}`);
      toast.success('Sale removed');
      invalidateActiveSales();
    } catch (error) { toast.error('Failed to remove sale'); }
  };

  const handleArchiveSale = async () => {
    if (!saleToArchive) return;
    setArchiveSaleDialogOpen(false);
    try {
      await api.put(`/sales/${saleToArchive.id}`, { archived: true });
      toast.success('Sale archived');
      setSaleToArchive(null);
      invalidateActiveSales();
    } catch (error) { toast.error('Failed to archive sale'); }
  };

  const handleUnarchiveSale = async (sale) => {
    try {
      await api.put(`/sales/${sale.id}`, { archived: false });
      toast.success('Sale unarchived');
      invalidateActiveSales();
    } catch (error) { toast.error('Failed to unarchive sale'); }
  };

  const handleCreateSession = (sale) => {
    setSelectedSale(sale);
    setEditingSession(null);
    setSessionForm({ sale_id: sale.id, name: '', date: '', ship_in_date: '', barn: '', time: '' });
    setSessionDialogOpen(true);
  };

  const handleEditSession = (sale, session) => {
    setSelectedSale(sale);
    setEditingSession(session);
    setSessionForm({
      sale_id: sale.id,
      name: session.name,
      date: (session.date || '').split('T')[0],
      ship_in_date: (session.ship_in_date || '').split('T')[0],
      barn: session.barn || '',
      time: session.time || ''
    });
    setSessionDialogOpen(true);
  };

  const handleSaveSession = async () => {
    if (!sessionForm.name || !sessionForm.date || !sessionForm.ship_in_date) {
      toast.error('Name, Session Date, and Ship In Date are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        sale_id: sessionForm.sale_id,
        name: sessionForm.name,
        date: sessionForm.date ? `${sessionForm.date}T12:00:00.000Z` : null,
        ship_in_date: sessionForm.ship_in_date ? `${sessionForm.ship_in_date}T12:00:00.000Z` : null,
        barn: sessionForm.barn || null,
        time: sessionForm.time || null
      };
      if (editingSession) {
        await api.put(`/sale-sessions/${editingSession.id}`, payload);
        toast.success('Session updated');
      } else {
        await api.post('/sale-sessions', payload);
        toast.success('Session created');
      }
      setSessionDialogOpen(false);
      invalidateSaleSessions(sessionForm.sale_id);
    } catch (error) { toast.error('Failed to save session'); }
    finally { setSaving(false); }
  };

  const handleDeleteSession = async () => {
    if (!editingSession) return;
    if (!await confirm({ title: `Delete session "${editingSession.name}"? This cannot be undone.`, destructive: true })) return;
    try {
      await api.delete(`/sale-sessions/${editingSession.id}`);
      toast.success('Session deleted');
      setSessionDialogOpen(false);
      invalidateSaleSessions(sessionForm.sale_id);
    } catch { toast.error('Failed to delete session'); }
  };

  const categorizeSales = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const preSale = [];
    const liveSale = [];
    const postSale = [];

    const filteredSales = activeSales.filter(sale => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      const saleName = sale.sale_name?.toLowerCase() || '';
      const saleCompany = sale.sale_company?.toLowerCase() || '';
      const location = sale.location?.toLowerCase() || '';
      const saleLocation = sale.sale_location?.toLowerCase() || '';
      const startDate = sale.start_date || sale.sale_start_date;
      const endDate = sale.end_date || sale.sale_end_date;
      let startDateStr = '';
      let endDateStr = '';
      if (startDate) startDateStr = new Date(startDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }).toLowerCase();
      if (endDate) endDateStr = new Date(endDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }).toLowerCase();
      return saleName.includes(searchLower) || saleCompany.includes(searchLower) || location.includes(searchLower) || saleLocation.includes(searchLower) || startDateStr.includes(searchLower) || endDateStr.includes(searchLower);
    });

    filteredSales.forEach(sale => {
      const startDateStr = sale.start_date || sale.sale_start_date;
      const endDateStr = sale.end_date || sale.sale_end_date || sale.start_date || sale.sale_start_date;
      if (!startDateStr) return;
      const startDate = new Date(startDateStr);
      const endDate = new Date(endDateStr);
      
      // Effective start is the earlier of sale start or earliest ship-in date
      let effectiveStart = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
      const saleSessions = sessions[sale.id] || [];
      saleSessions.forEach(s => {
        if (s.ship_in_date) {
          const shipDate = new Date(s.ship_in_date);
          const shipDay = new Date(shipDate.getFullYear(), shipDate.getMonth(), shipDate.getDate());
          if (shipDay < effectiveStart) effectiveStart = shipDay;
        }
      });
      
      const endDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
      endDay.setHours(23, 59, 59, 999);
      if (today < effectiveStart) preSale.push(sale);
      else if (today >= effectiveStart && today <= endDay) liveSale.push(sale);
      else postSale.push(sale);
    });

    // Pre-sale & live: ascending (soonest start date first)
    const asc = (a, b) => {
      const da = new Date(a.start_date || a.sale_start_date || '9999-12-31');
      const db = new Date(b.start_date || b.sale_start_date || '9999-12-31');
      return da - db;
    };
    // Post-sale: descending (most recent first)
    const desc = (a, b) => {
      const da = new Date(a.end_date || a.sale_end_date || a.start_date || a.sale_start_date || '0000-01-01');
      const db = new Date(b.end_date || b.sale_end_date || b.start_date || b.sale_start_date || '0000-01-01');
      return db - da;
    };
    preSale.sort(asc);
    liveSale.sort(asc);
    postSale.sort(desc);

    return { preSale, liveSale, postSale };
  };

  const { preSale, liveSale, postSale } = categorizeSales();

  // Auto-select tab: use URL param if provided, otherwise live if any, else presale
  const resolvedTab = isRestrictedRole ? 'live' : (activeTab || (liveSale.length > 0 ? 'live' : 'presale'));

  const renderSaleCard = (sale) => {
    // Default live sales to expanded
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = sale.start_date || sale.sale_start_date;
    const endDate = sale.end_date || sale.sale_end_date;
    let startDay = startDate ? new Date(new Date(startDate).getFullYear(), new Date(startDate).getMonth(), new Date(startDate).getDate()) : null;
    const endDay = endDate ? new Date(new Date(endDate).getFullYear(), new Date(endDate).getMonth(), new Date(endDate).getDate()) : startDay;
    if (endDay) endDay.setHours(23, 59, 59, 999);
    // Effective start includes earliest ship-in date
    const saleSessions = sessions[sale.id] || [];
    if (startDay) {
      saleSessions.forEach(s => {
        if (s.ship_in_date) {
          const shipDay = new Date(new Date(s.ship_in_date).getFullYear(), new Date(s.ship_in_date).getMonth(), new Date(s.ship_in_date).getDate());
          if (shipDay < startDay) startDay = shipDay;
        }
      });
    }
    const isLive = startDay && endDay && today >= startDay && today <= endDay;
    const expanded = expandedSales[sale.id] !== undefined ? expandedSales[sale.id] : isLive;

    return (
    <SaleCard
      key={sale.id}
      sale={sale}
      sessions={sessions[sale.id] || []}
      opportunities={opportunities[sale.id] || []}
      saleOwnerDeliverables={saleOwnerDeliverables[sale.id] || []}
      opportunityOwners={opportunityOwners}
      ownerEntities={ownerEntities}
      isExpanded={expanded}
      onToggleExpand={() => {
        const nowExpanded = !expanded;
        setExpandedSales(prev => ({ ...prev, [sale.id]: nowExpanded }));
        // deliverables already included in enrichment cache
      }}
      deadlinesVisible={deadlinesVisible[sale.id]}
      onToggleDeadlines={() => setDeadlinesVisible(prev => ({...prev, [sale.id]: prev[sale.id] === false}))}
      viewHistoric={viewHistoric}
      onArchive={(sale) => { setSaleToArchive(sale); setArchiveSaleDialogOpen(true); }}
      onUnarchive={handleUnarchiveSale}
      onDelete={handleDeleteSale}
      isTenantAdmin={isTenantAdmin}
      isCardRunner={isCardRunner}
      isSaleClerk={isSaleClerk}
      isViewOnly={isViewOnlyUser}
      onCreateSession={handleCreateSession}
      onEditSession={handleEditSession}
      onRefresh={() => invalidateSaleEnrichment(sale.id)}
    />
    );
  };

  return (
    <SidebarLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-serif text-3xl font-semibold">{isRestrictedRole ? 'Live Sales' : viewHistoric ? 'Historic Sales' : 'Active Sales'}</h1>
            <p className="text-muted-foreground mt-1">{isRestrictedRole ? 'Current live sales' : viewHistoric ? 'View archived sales' : 'Manage your consignment participation'}</p>
          </div>
          {!isRestrictedRole && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setViewHistoric(!viewHistoric)}>
              {viewHistoric ? 'View Active Sales' : 'View Historic'}
            </Button>
            {!viewHistoric && !isViewOnlyUser && (
              <Button onClick={() => setAddSaleDialogOpen(true)} className="bg-primary hover:bg-primary/90 text-white">
                <Plus className="w-4 h-4 mr-2" />
                Add Sale
              </Button>
            )}
          </div>
          )}
        </div>

        {/* Search Bar */}
        {!isRestrictedRole && (
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Search sales..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 w-full" />
              </div>
              <div className="flex justify-end">
              <TableToolbar
                inline
                tableId="active-sales"
                data={activeSales}
                filename="sales"
                columns={[
                  { key: 'sale_name', label: 'Sale Name' },
                  { key: 'sale_company', label: 'Company' },
                  { key: 'sale_start_date', label: 'Start Date', accessor: r => r.sale_start_date ? new Date(r.sale_start_date).toLocaleDateString() : '-' },
                  { key: 'sale_end_date', label: 'End Date', accessor: r => r.sale_end_date ? new Date(r.sale_end_date).toLocaleDateString() : '-' },
                  { key: 'location', label: 'Location' },
                ]}
              />
              </div>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Content */}
        {viewHistoric ? (
          <div className="space-y-4">
            {(() => {
              const filtered = activeSales.filter(sale => {
                if (!searchTerm) return true;
                const searchLower = searchTerm.toLowerCase();
                return (sale.sale_name?.toLowerCase() || '').includes(searchLower) ||
                       (sale.sale_company?.toLowerCase() || '').includes(searchLower) ||
                       (sale.location?.toLowerCase() || '').includes(searchLower);
              });
              return filtered.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Archive className="w-12 h-12 mx-auto mb-3 text-muted-foreground/20" />
                    <p className="text-muted-foreground">{searchTerm ? 'No matching archived sales' : 'No archived sales'}</p>
                  </CardContent>
                </Card>
              ) : filtered.map(sale => renderSaleCard(sale));
            })()}
          </div>
        ) : (
          <Tabs value={resolvedTab} onValueChange={isRestrictedRole ? undefined : setActiveTab}>
            {!isRestrictedRole && (
            <TabsList>
              <TabsTrigger value="presale">Pre-Sale ({preSale.length})</TabsTrigger>
              <TabsTrigger value="live">Live Sale ({liveSale.length})</TabsTrigger>
              <TabsTrigger value="postsale">Post-Sale ({postSale.length})</TabsTrigger>
            </TabsList>
            )}
            {!isRestrictedRole && (
            <TabsContent value="presale" className="space-y-4 mt-6">
              {preSale.length === 0 ? (
                <Card><CardContent className="py-12"><p className="text-center text-muted-foreground">No upcoming sales</p></CardContent></Card>
              ) : preSale.map(sale => renderSaleCard(sale))}
            </TabsContent>
            )}
            <TabsContent value="live" className="space-y-4 mt-6">
              {liveSale.length === 0 ? (
                <Card><CardContent className="py-12"><p className="text-center text-muted-foreground">No active sales</p></CardContent></Card>
              ) : liveSale.map(sale => renderSaleCard(sale))}
            </TabsContent>
            {!isRestrictedRole && (
            <TabsContent value="postsale" className="space-y-4 mt-6">
              {postSale.length === 0 ? (
                <Card><CardContent className="py-12"><p className="text-center text-muted-foreground">No completed sales</p></CardContent></Card>
              ) : postSale.map(sale => renderSaleCard(sale))}
            </TabsContent>
            )}
          </Tabs>
        )}
      </div>

      {/* Dialogs */}
      <AddSaleDialog
        open={addSaleDialogOpen}
        onOpenChange={setAddSaleDialogOpen}
        systemSales={systemSales}
        onJoinSale={handleJoinSale}
      />
      <SessionDialog
        open={sessionDialogOpen}
        onOpenChange={setSessionDialogOpen}
        sessionForm={sessionForm}
        setSessionForm={setSessionForm}
        selectedSale={selectedSale}
        editingSession={editingSession}
        saving={saving}
        onSave={handleSaveSession}
        onDelete={handleDeleteSession}
      />
      <ArchiveSaleDialog
        open={archiveSaleDialogOpen}
        onOpenChange={setArchiveSaleDialogOpen}
        saleToArchive={saleToArchive}
        onArchive={handleArchiveSale}
      />
    </SidebarLayout>
  );
};

export default ActiveSalesPage;
